import { LinkedList } from '@ocodelib/ex-util';
import { asReadonlyBS, Closables, createSetterBS } from '@ocodelib/ex-util/rx';
import { errmsg } from '@ocodelib/util/misc';
import type {
  IProjectLoader,
  ProjectFile,
  ProjectKey,
  ProjectLoadResult,
} from '@repo/project-interface';
import { DefaultProjectSerializer } from '@repo/project-serializer/impl';
import { toast } from 'react-toastify';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  Observable,
  of,
  switchMap,
  tap,
  throttleTime,
} from 'rxjs';
import type { IframeMonacoEditorType } from '../iframes';
import { NewProjectLoader } from '../project-loaders';
import type { JsBasicAppType } from '../types';
import { makeJsConsoleHook } from './actions/make-js-console-hook';
import { sketchToDocument } from './actions/sketch-to-document';
import type { ConsoleFeedLogType } from './types';

// 로그 지연시간: 0.3초 간격으로 로그 변경을 UI에 적용
const LOG_DELAY = 300;

// 최대 로그 건수
const LOG_MAX_CNT = 2000;

interface Options {
  ctx: { canceled: boolean };
  previewMode: boolean;
  newProjectLoader?: IProjectLoader<ProjectFile[]>;
  initialProjectLoader?: IProjectLoader<ProjectFile[]>;
}

export async function createJsBasicApp(options: Options, callback?: (app: JsBasicAppType) => void) {
  const {
    ctx,
    previewMode,
    newProjectLoader = new NewProjectLoader(),
    initialProjectLoader,
  } = options;
  const closables = new Closables();

  // iframe 요소
  const execIframeDom$ = new BehaviorSubject<HTMLIFrameElement | null>(null);

  // 파일 목록
  const files$ = new BehaviorSubject<ProjectFile[]>([]);

  // 에디터 인스턴스
  const editor$ = new BehaviorSubject<IframeMonacoEditorType | null>(null);

  const projectName$ = new BehaviorSubject<string>('새 프로젝트');

  // 선택된 파일
  const selectedFile$ = new BehaviorSubject<ProjectFile | null>(null);

  // 프로젝트 키
  const projectKey$ = new BehaviorSubject<ProjectKey | null>(null);

  // 파일의 내용이 변경되거나 파일명이 변경되는 경우 값이 발행된다.
  const fileChangedTimestamp$ = new BehaviorSubject(0);

  // 자동실행 여부
  const autoRefreshEnabled$ = new BehaviorSubject(true);

  const projectLoading$ = new BehaviorSubject(true);

  // 로그 표시 여부
  const logVisible$ = new BehaviorSubject(true);

  const logStore = new LinkedList<ConsoleFeedLogType>(LOG_MAX_CNT);
  const logs$ = new BehaviorSubject<ConsoleFeedLogType[]>([]);

  // 로그 일련 번호
  const logSeq$ = new BehaviorSubject(0);

  const blobUrlSet = new Set<string>();

  // 콘솔 훅
  const { consoleHookScriptUrl$, destroy: consoleHookDestroy } = makeJsConsoleHook();
  closables.add(consoleHookDestroy);

  // 모나코 에디터 설정
  const setupCodeEditor = (editor: IframeMonacoEditorType | undefined) => {
    destroyCodeEditor(); // destroy previous editor
    if (!editor) {
      editor$.next(null);
      return;
    }

    editor.setOptions({
      language: 'javascript',
      options: {
        theme: 'github-light',
        tabSize: 2,
        insertSpaces: true,
        fontSize: 16,
        automaticLayout: true,
        'semanticHighlighting.enabled': true,
        minimap: {
          enabled: false,
        },
      },
    });
    editor.setText(
      'console.log(`hello world`);\n for(let i=0;i<10;i++)\n\tconsole.log(1234);\n\n\n',
    );
    editor$.next(editor);
  };

  // 모나코 에디터 파괴
  const destroyCodeEditor = () => {
    const editor = editor$.value;
    if (editor) {
      editor.close();
      editor$.next(null);
    }
  };

  const isConsoleEvent = (evt: Event) => {
    if (!('data' in evt)) return false;
    const data = evt.data as any;
    if (typeof data !== 'object') return false;
    return data['type'] === 'console' && typeof data['method'] === 'string' && 'value' in data;
  };

  // 로그 메시지 핸들러
  const onLogMessage = (ev: MessageEvent) => {
    const iframe = execIframeDom$.value;
    if (!iframe) return;
    if (isConsoleEvent(ev)) {
      const { method, value } = ev.data;
      // console.log("XXX onLogMessage:", ev);
      const logSeq = (logSeq$.value + 1) % Number.MAX_SAFE_INTEGER;
      const log: ConsoleFeedLogType = {
        data: value,
        id: String(logSeq),
        method,
      };
      if (method in console) {
        (console as any)[method](value as any);
        logStore.append(log);
      } else {
        console.warn('unknown console message', ev.data);
      }
      logSeq$.next(logSeq);
    }
  };

  // 콘솔 로그 지우기
  const clearLogs = () => {
    logStore.clear();
    logs$.next([]);
  };

  // iframe에 스케치 렌더링
  const renderSketch = () => {
    const files = files$.value;
    const iframe = execIframeDom$.value;
    if (!iframe) {
      console.log('renderSketch() skip, iframe is null');
      return;
    }

    const consoleHookScriptUrl = consoleHookScriptUrl$.value;
    if (!consoleHookScriptUrl) {
      console.log('renderSketch() skip, consoleHookScriptUrl not initialized');
      return;
    }

    if (files.length <= 0) {
      console.log('renderSketch() skip, editor file not exists');
      return;
    }

    revokeLastBlobUrls();

    const doc = sketchToDocument(files, blobUrlSet);
    if (doc) {
      // Add Scripts to Catch console error
      const consoleScriptTag = doc.createElement('script');
      // consoleScriptTag.innerHTML = consoleHookScriptUrl;
      consoleScriptTag.src = consoleHookScriptUrl;
      doc.head.insertBefore(consoleScriptTag, doc.head.firstChild);
      clearLogs();
      iframe.srcdoc = `<!DOCTYPE HTML>\n${doc.documentElement.outerHTML}`;
    }
  };

  function revokeLastBlobUrls() {
    if (blobUrlSet.size === 0) return;
    Array.from(blobUrlSet).forEach((it) => {
      URL.revokeObjectURL(it);
    });
    blobUrlSet.clear();
  }

  // 실행 중지
  const stopSketch = () => {
    if (execIframeDom$.value) {
      execIframeDom$.value.srcdoc = '';
    }
    autoRefreshEnabled$.next(false);
  };

  async function serializeToPjtFile(): Promise<File | null> {
    const files = files$.value;
    if (files.length === 0) {
      // 이런 경우는 없음
      toast.warn('저장할 파일이 없습니다');
      return null;
    }

    const fileObjects = files.map((it) => {
      const blob = new Blob([it.content]);
      return new File([blob], it.name, { type: 'text/plain', lastModified: Date.now() });
    });
    const projectKey = projectKey$.value;
    return await DefaultProjectSerializer.serializePjtFile(
      {
        breed: 'js',
        projectName: projectName$.value,
        projectKey: projectKey ?? undefined,
      },
      fileObjects,
    );
  }

  /**
   * 프로젝트 리셋
   * 로드한 프로젝트는 소유자가 있다면 parent 프로젝트이다.
   * currentProjectKey는 현재까지는 항상 undefined이다.
   * 만약 서버에 저장한 후에는 currentProjectKey에 값이 설정된다.
   * 그후 저장하면 계속 currentProjectKey가 유지된다.
   *
   * 이 상태에서 로그아웃을 해도 projectKey는 그대로 유지한다.
   * 이 상태에서 로그인을 한 후 저장을 하면
   * currentProjectKey가 본인의 것이라면 현재 프로젝트를 업데이트하고,
   * currentProjectKey가 타인의 것이라면 새로운 프로젝트로 저장한다.
   *
   * 여기서 중요한 것은 current가 타인의 프로젝트일 수 있는 점이다.
   * parent는 신규저장을 할 때만 필요하다.
   * 즉, 이 프로젝트를 저장할 떄 parent를 참조한 사실을 기록한다.
   * current가 설정되었다면, parent는 더이상 필요없다.
   * 따라서 저장한 후에는 parent를 제거해야 한다.
   * @param projectLoadResult - 로드 결과
   */
  function resetProject(projectLoadResult: ProjectLoadResult<ProjectFile[]>) {
    const { projectName, data: projectFiles, project } = projectLoadResult;
    files$.next(projectFiles);
    projectName$.next(projectName);
    projectKey$.next(project ?? null);
  }

  async function resetToNewProject() {
    try {
      projectLoading$.next(true);
      const result = await newProjectLoader.load();
      resetProject(result);
    } catch (err) {
      toast.warn('기본 프로젝트 로드 실패:' + errmsg(err));
    } finally {
      projectLoading$.next(false);
    }
  }

  // destroy app
  const destroy = () => {
    console.log('XXX destroy() called');
    window.removeEventListener('message', onLogMessage);
    closables.close();
    destroyCodeEditor();
    revokeLastBlobUrls();
  };

  // iframe to model: iframe 에디터의 내용 변경 감시
  const observeEditorTextChanged = (editor: IframeMonacoEditorType, currentFile: ProjectFile) => {
    return new Observable<string>((observer) => {
      const onTextChanged = (text: string) => {
        if (currentFile.content !== text) {
          currentFile.content = text;
          observer.next(text);
        }
      };
      editor.iframeController.events.on('text-changed', onTextChanged);
      return () => {
        editor.iframeController.events.off('text-changed', onTextChanged);
      };
    });
  };

  closables.addRx(
    // iframe to model: iframe 에디터의 내용 변경 감시
    combineLatest([selectedFile$, editor$], (selectedFile, editor) => {
      return editor && selectedFile ? { selectedFile, editor } : null;
    })
      .pipe(
        switchMap((it) => {
          if (!it) return of(null);
          const { editor, selectedFile } = it;
          return observeEditorTextChanged(editor, selectedFile);
        }),
        distinctUntilChanged(),
        tap(() => {
          fileChangedTimestamp$.next((fileChangedTimestamp$.value + 1) % Number.MAX_SAFE_INTEGER);
        }),
      )
      .subscribe(),

    // model to iframe: 선택된 파일이 변경되면 에디터에 표시한다
    combineLatest([selectedFile$, editor$], (selectedFile, editor) => {
      return editor ? { selectedFile, editor } : null;
    })
      .pipe(filter(Boolean))
      .subscribe(({ selectedFile, editor }) => {
        if (!selectedFile) {
          editor.setText('');
        } else {
          editor.setText(selectedFile.content);
        }
      }),

    // 변경이 생기면 스케치 렌더링
    combineLatest(
      [
        // html file이 존재하는지 체크
        files$.pipe(
          map((files) => files.find((f) => f.name.match(/.*\.html$/))),
          distinctUntilChanged(),
        ),
        // 실행용 iframe
        execIframeDom$,

        // 자동실행 여부
        autoRefreshEnabled$,

        // consoleHookScriptUrl 준비
        consoleHookScriptUrl$,

        // 파일의 내용 변경 체크
        fileChangedTimestamp$,
      ],
      (htmlExists, iframe, autoRunEnabled, consoleHookScriptUrl, _fileChangeTime) => {
        return !!htmlExists && !!iframe && autoRunEnabled && !!consoleHookScriptUrl;
      },
    )
      .pipe(filter(Boolean), debounceTime(1000))
      .subscribe(() => {
        renderSketch();
      }),

    // 파일 목록이 변경되면, selectedFile을 변경한다
    files$.subscribe((files) => {
      if (files.length === 0) {
        selectedFile$.next(null);
      } else {
        if (!selectedFile$.value) {
          selectedFile$.next(files[0]!);
          return;
        }

        if (!files.some((it) => it === selectedFile$.value)) {
          selectedFile$.next(files[0]!);
          return;
        }
      }
    }),

    // iframe이 변경되면, 로그 감시를 한다
    execIframeDom$
      .pipe(
        distinctUntilChanged(),
        finalize(() => {
          window.removeEventListener('message', onLogMessage);
        }),
      )
      .subscribe((iframe) => {
        if (iframe) {
          window.addEventListener('message', onLogMessage);
        } else {
          window.removeEventListener('message', onLogMessage);
        }
      }),

    // n 밀리초 간격으로 로그 변경을 UI에 적용
    logSeq$.pipe(throttleTime(LOG_DELAY, undefined, { trailing: true })).subscribe(() => {
      logs$.next(logStore.toArray());
    }),
  );

  if (initialProjectLoader) {
    (async () => {
      try {
        projectLoading$.next(true);
        const result = await initialProjectLoader.load();
        resetProject(result);
      } catch (err) {
        toast.warn('프로젝트 로드 실패:' + errmsg(err));
      } finally {
        projectLoading$.next(false);
      }
    })();
  } else {
    if (!previewMode) {
      // 최초 기본 파일 생성
      resetToNewProject();
    }
  }

  const result = {
    execIframeDom$: asReadonlyBS(execIframeDom$),
    setExecIframeDom: createSetterBS(execIframeDom$),

    selectedFile$: asReadonlyBS(selectedFile$),
    setSelectedFile: createSetterBS(selectedFile$),

    logVisible$: asReadonlyBS(logVisible$),
    setLogVisible: createSetterBS(logVisible$),

    projectLoading$: asReadonlyBS(projectLoading$),

    /**
     * 파일 목록
     */
    files$: asReadonlyBS(files$),
    resetProject,

    /**
     * 에디터 - IframeMonacoEditorType
     */
    setupCodeEditor,
    destroyCodeEditor,
    editor$: asReadonlyBS(editor$),

    logs$: asReadonlyBS(logs$),

    /**
     * 프로젝트 키
     * 서버에 저장된 프로젝트가 아닌 경우 null
     * 다른 소유자의 프로젝트일 수 있음
     */
    setProjectKey: createSetterBS(projectKey$),
    projectKey$: asReadonlyBS(projectKey$),

    /**
     * 프로젝트 이름
     */
    setProjectName: createSetterBS(projectName$),
    projectName$: asReadonlyBS(projectName$),

    /**
     * 자동실행 여부
     */
    setAutoRefreshEnabled: createSetterBS(autoRefreshEnabled$),
    autoRefreshEnabled$: asReadonlyBS(autoRefreshEnabled$),

    /**
     * 현재 프로젝트를 파일로 저장
     */
    serializeToPjtFile,

    /**
     * 새로운 프로젝트로 리셋
     */
    resetToNewProject,

    renderSketch,
    stopSketch,
    clearLogs,
    destroy,
  };

  if (ctx.canceled) {
    destroy();
  } else {
    callback?.(result);
  }

  return result;
}
